import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, BadgeCheck, CalendarCheck, Clock3, Loader2, MessageCircle, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.16, 1, 0.3, 1];

const SERVICE_TYPES = [
  "Film & OTT",
  "Documentary",
  "Commercial",
  "Real Estate",
  "Drone Services",
  "General Consultation",
];

const inputDark =
  "w-full bg-white/[0.05] border border-white/15 focus:border-[#C9A227] outline-none rounded-xl px-5 py-4 text-[15px] text-white placeholder:text-white/35 transition-colors duration-300";

export const ConsultationSection = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service_type: "", date: "", time: "", brief: "" });
  const [sending, setSending] = useState(false);
  const [doneId, setDoneId] = useState(null);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async () => {
    if (form.name.trim().length < 2) return toast.error("Apna naam daalo");
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return toast.error("Valid email daalo");
    if (form.phone.trim().length < 10) return toast.error("Valid phone number daalo");
    if (!form.service_type) return toast.error("Service type choose karo");
    if (!form.date || !form.time) return toast.error("Preferred date aur time daalo");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/consultations`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        service_type: form.service_type,
        preferred_datetime: `${form.date}T${form.time}`,
        brief: form.brief.trim(),
      });
      setDoneId(data.consult_id);
      toast.success("Consultation request received!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kuch gadbad ho gayi — dobara try karo");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="consultation" data-testid="services-consultation" className="relative bg-[#0a0a0a] py-[90px] md:py-[120px] overflow-hidden">
      {/* gold ambient glows */}
      <div aria-hidden className="absolute -top-32 -right-24 w-96 h-96 rounded-full bg-[#C9A227]/10 blur-3xl" />
      <div aria-hidden className="absolute -bottom-32 -left-24 w-96 h-96 rounded-full bg-[#C9A227]/[0.07] blur-3xl" />

      <div className="relative z-10 max-w-[1320px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-14 items-center">
        {/* Left — copy */}
        <motion.div initial={{ opacity: 0, y: 34 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.8, ease }}>
          <p className="font-mono text-[14px] tracking-[0.3em] uppercase text-[#C9A227] flex items-center gap-4">
            <span className="w-10 h-px bg-[#C9A227]/60 inline-block" /> Free Consultation
          </p>
          <h2 className="mt-5 font-serif text-[36px] md:text-[52px] leading-[1.05] text-white">
            Let's Plan Your <span className="bg-gradient-to-r from-[#C9A227] via-[#F5C04E] to-[#FF8C3D] bg-clip-text text-transparent">Next Shoot</span>
          </h2>
          <p className="mt-5 text-[15px] text-white/60 leading-relaxed max-w-md">
            Project ka idea share karo — main 24 hours ke andar personally call karke approach, budget aur timeline discuss karta hoon. No obligation, bas ek achhi baat-cheet.
          </p>
          <ul className="mt-8 space-y-4">
            {[
              { icon: Clock3, t: "Reply within 24 hours" },
              { icon: ShieldCheck, t: "No advance needed for consultation" },
              { icon: BadgeCheck, t: "Gov + OTT production experience" },
            ].map((x) => {
              const Icon = x.icon;
              return (
                <li key={x.t} className="flex items-center gap-3.5">
                  <span className="w-9 h-9 rounded-xl bg-[#C9A227]/15 border border-[#C9A227]/30 flex items-center justify-center shrink-0">
                    <Icon size={15} className="text-[#C9A227]" />
                  </span>
                  <p className="font-mono text-[12px] tracking-[0.12em] uppercase text-white/70">{x.t}</p>
                </li>
              );
            })}
          </ul>
        </motion.div>

        {/* Right — form card */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.12, ease }}
          className="bg-[#111111] border border-white/10 rounded-[24px] p-7 md:p-9 shadow-[0_50px_100px_-30px_rgba(0,0,0,0.8)]"
          data-testid="consult-form-card"
        >
          {doneId ? (
            <div className="text-center py-8" data-testid="consult-success">
              <span className="mx-auto w-16 h-16 rounded-full bg-[#C9A227]/15 border border-[#C9A227]/40 flex items-center justify-center">
                <BadgeCheck size={28} className="text-[#C9A227]" />
              </span>
              <h3 className="mt-6 font-serif text-3xl text-white">Request Received!</h3>
              <p className="mt-3 text-sm text-white/55 leading-relaxed">
                Consultation ID <span className="font-mono font-bold text-[#C9A227]">{doneId}</span> — main 24 hours mein <span className="text-white">{form.phone}</span> par call karunga. Email confirmation aa rahi hai.
              </p>
              <button
                data-testid="consult-book-another"
                onClick={() => { setDoneId(null); setForm({ name: "", email: "", phone: "", service_type: "", date: "", time: "", brief: "" }); }}
                className="mt-7 inline-flex items-center gap-2 border border-white/20 text-white/70 font-mono text-[11px] font-bold tracking-[0.18em] uppercase px-7 py-3.5 rounded-xl hover:border-[#C9A227] hover:text-[#C9A227] transition-colors"
              >
                Book Another
              </button>
            </div>
          ) : (
            <>
              <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#C9A227] flex items-center gap-2.5">
                <CalendarCheck size={14} /> Book Your Slot
              </p>
              <div className="mt-6 space-y-3.5">
                <input data-testid="consult-name" value={form.name} onChange={set("name")} placeholder="Full Name" className={inputDark} />
                <div className="grid sm:grid-cols-2 gap-3.5">
                  <input data-testid="consult-email" type="email" value={form.email} onChange={set("email")} placeholder="Email" className={inputDark} />
                  <input data-testid="consult-phone" value={form.phone} onChange={set("phone")} placeholder="Phone Number" className={inputDark} />
                </div>
                <select data-testid="consult-service" value={form.service_type} onChange={set("service_type")} className={`${inputDark} cursor-pointer ${form.service_type ? "" : "text-white/35"}`}>
                  <option value="" disabled className="bg-[#111]">Service Type — choose karo</option>
                  {SERVICE_TYPES.map((s) => (
                    <option key={s} value={s} className="bg-[#111] text-white">{s}</option>
                  ))}
                </select>
                <div className="grid grid-cols-2 gap-3.5">
                  <input data-testid="consult-date" type="date" value={form.date} onChange={set("date")} className={`${inputDark} [color-scheme:dark]`} />
                  <input data-testid="consult-time" type="time" value={form.time} onChange={set("time")} className={`${inputDark} [color-scheme:dark]`} />
                </div>
                <textarea data-testid="consult-brief" value={form.brief} onChange={set("brief")} rows={4} placeholder="Project Brief — kya banana hai, kab, kahan…" className={`${inputDark} resize-y`} />
              </div>
              <button
                data-testid="consult-submit"
                onClick={submit}
                disabled={sending}
                className="mt-6 w-full inline-flex items-center justify-center gap-2.5 bg-gradient-to-r from-[#C9A227] to-[#FF8C3D] text-black font-mono text-[12px] font-bold tracking-[0.18em] uppercase px-8 py-4.5 rounded-xl py-4 hover:shadow-[0_15px_40px_-10px_rgba(201,162,39,0.5)] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60"
              >
                {sending ? <Loader2 size={15} className="animate-spin" /> : <MessageCircle size={15} />}
                {sending ? "Booking…" : "Book My Consultation"} <ArrowRight size={14} />
              </button>
              <p className="mt-4 text-center font-mono text-[10px] tracking-[0.15em] uppercase text-white/35">
                Free hai — payment baad mein, pehle baat-cheet
              </p>
            </>
          )}
        </motion.div>
      </div>
    </section>
  );
};
